import type { CSSProperties, ReactNode } from 'react';
import { Diamond, Triangle, CheckCircle2 } from 'lucide-react';
import {
  getIndicator,
  type StatusIndicator,
  type StatusIndicatorShape,
} from './status-indicators';

// StatusPip — renders any catalogued indicator with its canonical shape +
// colour + a hover tooltip wired via the `.tt-below` recipe in global.css.
//
// Spec: docs/logic/workspace/status-indicators.md
// Action: greg-tim-2026-05-18.A4
//
// Two consumption modes:
//   1. <StatusPip indicatorId="chapter-drafted" />
//      Renders the indicator's canonical visual + tooltip from the catalogue.
//   2. <StatusTooltip indicatorId="chapter-drafted">{children}</StatusTooltip>
//      Wraps an existing pip (e.g. a hand-rolled <Diamond/>) with just the
//      tooltip — for surfaces we don't want to swap the visual on, only add
//      a tooltip to. The wrapper uses the same `.tt-below` recipe.
//
// Tooltip implementation:
//   The `.tt-below` recipe lives in src/styles/global.css. It reads from
//   `aria-label` and renders a 6px-gap popover below the element on hover or
//   focus. Reusing it keeps the chrome consistent with top-bar buttons.

export type StatusPipProps = {
  /** Catalogue id — see STATUS_INDICATORS in status-indicators.ts. */
  indicatorId: string;
  /** Optional size override — defaults to the per-shape recipe size. */
  size?: number;
  /** Optional className passthrough on the outer wrapper. */
  className?: string;
  /** Optional inline style passthrough on the outer wrapper. */
  style?: CSSProperties;
  /** When true, the parent already paints a tooltip (e.g. a button row with
   *  `title=` set). Skip wiring `.tt-below` to avoid double tooltips. */
  noTooltip?: boolean;
  /** Override the tooltip label (defaults to the catalogue label). Useful
   *  when the row owns richer context (e.g. "Drafted · Owner H. Joubert"). */
  tooltipOverride?: string;
};

const SHAPE_DEFAULT_SIZE: Record<StatusIndicatorShape, number> = {
  'dot-filled': 6,
  'dot-hollow': 6,
  'square-filled': 8,
  'square-hollow': 12,
  diamond: 10,
  triangle: 10,
  pill: 0, // pills size to their text
  'meter-band': 0,
  'check-pill': 0,
};

// Render the bare visual element — no tooltip, no wrapper. The tooltip is
// applied by the consumer (either StatusPip below or StatusTooltip).
export function StatusPipGlyph({
  indicator,
  size,
  style,
}: {
  indicator: StatusIndicator;
  size?: number;
  style?: CSSProperties;
}) {
  const { shape, colorToken, accent, borderStyle = 'solid' } = indicator.visual;
  const px = size ?? (SHAPE_DEFAULT_SIZE[shape] || 10);
  const color = `var(${colorToken})`;

  if (shape === 'dot-filled') {
    return (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: px,
          height: px,
          borderRadius: '999px',
          background: color,
          flexShrink: 0,
          ...style,
        }}
      />
    );
  }
  if (shape === 'dot-hollow') {
    return (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: px,
          height: px,
          borderRadius: '999px',
          border: `1px ${borderStyle} ${color}`,
          background: 'transparent',
          flexShrink: 0,
          ...style,
        }}
      />
    );
  }
  if (shape === 'square-filled') {
    return (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: px,
          height: px,
          background: color,
          flexShrink: 0,
          ...style,
        }}
      />
    );
  }
  if (shape === 'square-hollow') {
    return (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: px,
          height: px,
          border: `1px ${borderStyle} ${color}`,
          background: 'transparent',
          flexShrink: 0,
          ...style,
        }}
      />
    );
  }
  if (shape === 'diamond') {
    return (
      <Diamond
        size={px + 2}
        style={{ color, fill: color, flexShrink: 0, ...style }}
        aria-hidden
      />
    );
  }
  if (shape === 'triangle') {
    return (
      <Triangle
        size={px + 2}
        style={{ color, flexShrink: 0, ...style }}
        aria-hidden
      />
    );
  }
  if (shape === 'check-pill') {
    return (
      <span
        aria-hidden
        className="inline-flex items-center gap-0.5 tabular"
        style={{
          fontSize: 10,
          padding: '0 5px',
          borderRadius: 3,
          background: `color-mix(in srgb, ${color} 16%, transparent)`,
          color,
          lineHeight: '14px',
          fontWeight: 500,
          ...style,
        }}
      >
        <CheckCircle2 size={9} aria-hidden />
        TEG-ready
      </span>
    );
  }
  if (shape === 'pill') {
    return (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          padding: '2px 6px',
          borderRadius: 3,
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '0.02em',
          color,
          background: accent ? `var(${accent})` : 'var(--surface-3)',
          ...style,
        }}
      >
        {indicator.label}
      </span>
    );
  }
  // meter-band — caller usually renders its own meter, fall back to a
  // small filled square.
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: px || 6,
        height: 6,
        background: color,
        ...style,
      }}
    />
  );
}

/**
 * StatusPip — renders the indicator's canonical visual and wires up the
 * `.tt-below` tooltip. The tooltip reads from `aria-label` (the catalogue
 * `label`). Set `noTooltip` to skip the wrapper when the parent button or row
 * already owns the tooltip.
 */
export function StatusPip({
  indicatorId,
  size,
  className,
  style,
  noTooltip,
  tooltipOverride,
}: StatusPipProps) {
  const indicator = getIndicator(indicatorId);
  // Default tooltip = "<label> — <description>" so the hover explains the
  // indicator semantics, not just its display label. Callers that want a
  // short or contextual string pass `tooltipOverride`.
  const tooltip =
    tooltipOverride ?? `${indicator.label} — ${indicator.description}`;

  if (noTooltip) {
    return (
      <span
        className={['inline-flex items-center', className].filter(Boolean).join(' ')}
        style={style}
      >
        <StatusPipGlyph indicator={indicator} size={size} />
      </span>
    );
  }

  return (
    <span
      className={['tt-below tt-wrap inline-flex items-center', className].filter(Boolean).join(' ')}
      style={{ ...style, lineHeight: 0 }}
      aria-label={tooltip}
      tabIndex={0}
      role="img"
    >
      <StatusPipGlyph indicator={indicator} size={size} />
    </span>
  );
}

/**
 * StatusTooltip — wraps an existing pip / glyph with the catalogue tooltip
 * without changing its visual. Use this for indicators we don't want to
 * re-render through StatusPip (e.g. a custom-styled badge or a row-level
 * status icon embedded in a button).
 */
export function StatusTooltip({
  indicatorId,
  tooltipOverride,
  className,
  style,
  children,
}: {
  indicatorId: string;
  tooltipOverride?: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const indicator = getIndicator(indicatorId);
  const tooltip =
    tooltipOverride ?? `${indicator.label} — ${indicator.description}`;
  return (
    <span
      className={['tt-below tt-wrap inline-flex items-center', className].filter(Boolean).join(' ')}
      style={{ ...style, lineHeight: 0 }}
      aria-label={tooltip}
      tabIndex={0}
      role="img"
    >
      {children}
    </span>
  );
}
